import { ColumnDef } from '@tanstack/react-table'

import { Badge, Icon, Tooltip, TooltipContent, TooltipTrigger } from '@hap/components'
import { startCase } from '@hap/utils'

import { MakeColumnOptions } from './types'

export function makeLabelColumnTrpc<D extends UnknownRecord>({
  fieldKey,
  header = startCase(fieldKey as string),
  minWidth = 200,
}: MakeColumnOptions<D>): ColumnDef<D> {
  return {
    id: fieldKey as string,
    header,
    size: minWidth,
    minSize: minWidth,
    maxSize: minWidth,
    accessorKey: fieldKey,
    cell: (cell) => {
      const label = cell.getValue<{ label: string; id: string; color?: string }>()
      if (!label) return null
      return (
        <div className="flex items-center gap-2 truncate">
          <div
            className="aspect-square h-4 shrink-0 rounded-full"
            style={{ backgroundColor: label.color }}
          />
          {label.label}
        </div>
      )
    },
  }
}

export function makeFlagColumnTrpc<D extends UnknownRecord>({
  fieldKey,
  header = startCase(fieldKey as string),
  minWidth = 200,
}: MakeColumnOptions<D>): ColumnDef<D> {
  return {
    id: fieldKey as string,
    header,
    size: minWidth,
    minSize: minWidth,
    maxSize: minWidth,
    accessorKey: fieldKey,
    cell: (cell) => {
      const label = cell.getValue<{ name: string; id: string; color?: string }>()
      if (!label) return null
      return (
        <div className="flex items-center gap-2 truncate">
          <Icon
            name="flag"
            className="shrink-0"
            size={16}
            weight="fill"
            color={label.color}
          />
          {label.name}
        </div>
      )
    },
  }
}

export function makeSentimentColumnTrpc<D extends UnknownRecord>({
  fieldKey,
  header = startCase(fieldKey as string),
  width = 210,
  minWidth = width,
  maxWidth,
}: MakeColumnOptions<D>): ColumnDef<D> {
  return {
    id: fieldKey as string,
    header,
    size: width,
    minSize: minWidth,
    maxSize: maxWidth,
    accessorKey: fieldKey,
    cell: (cell) => {
      const cellValue = cell.getValue<{
        label: { label: string; id: string; color?: string }
        value: number
      }>()
      if (!cellValue || !cellValue.label) return null
      const { value, label } = cellValue
      if (!value)
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <SentimentIconV2 value={label.label} />
            {label.label}
          </div>
        )
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <SentimentIcon value={value} />
              {scoreToLabel(value)}
            </div>
          </TooltipTrigger>
          <TooltipContent>Score: {value}</TooltipContent>
        </Tooltip>
      )
    },
  }
}

function SentimentIcon(props: { value: number }) {
  if (props.value > 0.5)
    return (
      <div className="bg-green/90 flex aspect-square h-6 items-center justify-center rounded-md pt-px text-center">
        😁
      </div>
    )
  if (props.value > 0.25)
    return (
      <div className="bg-green/50 flex aspect-square h-6 items-center justify-center rounded-md pt-px text-center">
        😌
      </div>
    )
  if (props.value < -0.25)
    return (
      <div className="bg-red/50 flex aspect-square h-6 items-center justify-center rounded-md pt-px text-center">
        🙁
      </div>
    )
  if (props.value < -0.5)
    return (
      <div className="bg-red flex aspect-square h-6 items-center justify-center rounded-md pt-px text-center">
        😡
      </div>
    )
  return (
    <div className="bg-foreground/20 flex aspect-square h-6 items-center justify-center rounded-md pt-px text-center">
      😐
    </div>
  )
}

function SentimentIconV2(props: { value: string }) {
  if (props.value === 'Positive')
    return (
      <div className="bg-green/90 flex aspect-square h-6 items-center justify-center rounded-md pt-px text-center">
        😁
      </div>
    )

  if (props.value === 'Negative')
    return (
      <div className="bg-red/50 flex aspect-square h-6 items-center justify-center rounded-md pt-px text-center">
        😡
      </div>
    )
  return (
    <div className="bg-foreground/20 flex aspect-square h-6 items-center justify-center rounded-md pt-px text-center">
      😐
    </div>
  )
}

function scoreToLabel(value: number) {
  if (value > 0.5) return 'Very Positive'
  if (value > 0.25) return 'Positive'
  if (value < -0.25) return 'Negative'
  if (value < -0.5) return 'Very Negative'
  return 'Neutral'
}

export function makeLabelsColumnTrpc<D extends UnknownRecord>({
  fieldKey,
  header = startCase(fieldKey as string),
  width = 360,
  minWidth = width,
  maxWidth,
}: MakeColumnOptions<D>): ColumnDef<D> {
  return {
    id: fieldKey as string,
    header,
    size: width,
    minSize: minWidth,
    maxSize: maxWidth,
    accessorKey: fieldKey,
    cell: (cell) => {
      const labels =
        cell.getValue<Nullable<{ label: string; id: string; color?: string }[]>>()
      if (!labels || !labels.length) return null
      return (
        <div className="flex items-center gap-2 truncate">
          {labels.map((label) => (
            <Badge key={label.id} color={label.color}>
              {label.label}
            </Badge>
          ))}
        </div>
      )
    },
  }
}
